import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { AddressOnboardingCard } from './AddressOnboardingCard';
import { UpgradePlanModal } from './UpgradePlanModal';
import { ForYouRecommendations } from './ForYouRecommendations';
import { DiscountClaimsCard } from './DiscountClaimsCard';
import { NewsWidget } from './NewsWidget';
import { SavingsStrip } from './member-dashboard/SavingsStrip';
import { MerchantPartnerStatusBanner } from './member-dashboard/MerchantPartnerStatusBanner';
import { StatsCards } from './member-dashboard/StatsCards';
import { ValueGrowthChart } from './member-dashboard/ValueGrowthChart';
import { ReferralProgramCard } from './member-dashboard/ReferralProgramCard';
import { QuickActions } from './member-dashboard/QuickActions';
import { RecentActivity } from './member-dashboard/RecentActivity';

export function MemberDashboard() {
  const { user, api } = useAuth();
  const { lastMessage } = useWebSocket();
  const [wallet, setWallet] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referralCfg, setReferralCfg] = useState({ max_levels: 2, level_1_rate: 10, level_2_rate: 5, level_3_rate: 0 });
  const [transactions, setTransactions] = useState([]);
  const [subTransactions, setSubTransactions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [maxTier, setMaxTier] = useState(false);
  const [savingsSummary, setSavingsSummary] = useState({ total: 0, total_savings: 0 });

  // --- Fetchers ----------------------------------------------------------
  const fetchWallet = async () => {
    try {
      const { data } = await api.get('/api/wallet/balance');
      setWallet(data);
    } catch (e) { /* non-blocking */ }
  };

  const fetchReferralStats = async () => {
    try {
      const { data } = await api.get('/api/referrals/stats');
      setReferralStats(data);
    } catch (e) { /* non-blocking */ }
  };

  const fetchReferralConfig = async () => {
    try {
      const { data } = await api.get('/api/config/referral');
      setReferralCfg({
        max_levels: Math.max(1, Math.min(3, Number(data?.max_levels) || 2)),
        level_1_rate: Number(data?.level_1_rate) || 0,
        level_2_rate: Number(data?.level_2_rate) || 0,
        level_3_rate: Number(data?.level_3_rate) || 0,
      });
    } catch (e) { /* keep defaults */ }
  };

  const fetchTransactions = async () => {
    try {
      const { data } = await api.get('/api/wallet/transactions');
      setTransactions(data.transactions || []);
    } catch (e) { /* non-blocking */ }
  };

  const fetchSubTransactions = async () => {
    try {
      const { data } = await api.get('/api/subscriptions/transactions');
      setSubTransactions(data.transactions || []);
    } catch (e) { /* non-blocking */ }
  };

  const fetchSavingsSummary = async () => {
    try {
      const { data } = await api.get('/api/services/my-claims?skip=0&limit=1');
      setSavingsSummary({ total: data?.total || 0, total_savings: data?.total_savings || 0 });
    } catch (e) { /* counters fall back to 0 */ }
  };

  // Decide whether the "Upgrade Plan" Quick Action should be disabled —
  // happens when the user already holds the most expensive active plan.
  const fetchMaxTierStatus = async () => {
    if (!user || user.subscription_status !== 'active' || !user.subscription_plan) {
      setMaxTier(false);
      return;
    }
    try {
      const { data } = await api.get('/api/subscriptions/plans');
      const active = (data?.plans || []).filter((p) => p.is_active !== false);
      if (!active.length) return;
      const currentPrice = Number(active.find((p) => p.name === user.subscription_plan)?.price || 0);
      const topPrice = Math.max(...active.map((p) => Number(p.price || 0)));
      setMaxTier(currentPrice >= topPrice);
    } catch (e) { /* non-blocking */ }
  };

  const fetchDashboardData = async () => {
    await Promise.all([
      fetchWallet(),
      fetchReferralStats(),
      fetchReferralConfig(),
      fetchTransactions(),
      fetchSubTransactions(),
      fetchMaxTierStatus(),
      fetchSavingsSummary(),
    ]);
  };

  useEffect(() => { fetchDashboardData(); /* eslint-disable-next-line */ }, []);

  // Live wallet refresh on portfolio_update WS events.
  useEffect(() => {
    if (lastMessage?.type === 'portfolio_update') {
      fetchWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPositiveGrowth = (wallet?.growth_percentage || 0) >= 0;

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8">
        <AddressOnboardingCard />

        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#A0B4CC] mt-1">
            {user?.subscription_status === 'active' ? 'Your membership is active' : 'Subscribe to unlock all benefits'}
          </p>
        </div>

        <SavingsStrip savingsSummary={savingsSummary} user={user} />

        <MerchantPartnerStatusBanner user={user} api={api} />

        <StatsCards wallet={wallet} isPositiveGrowth={isPositiveGrowth} />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ValueGrowthChart wallet={wallet} />
          </div>
          <div>
            <ReferralProgramCard
              user={user}
              referralCfg={referralCfg}
              referralStats={referralStats}
              copied={copied}
              onCopy={copyReferralLink}
            />
          </div>
        </div>

        <ForYouRecommendations defaultLimit={3} />

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <QuickActions user={user} maxTier={maxTier} onOpenUpgrade={() => setUpgradeOpen(true)} />
          <RecentActivity transactions={transactions} subTransactions={subTransactions} />
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 rounded-lg bg-[#041A3B]/50 border border-white/5">
          <p className="text-[#5C7599] text-xs text-center">
            <strong>Disclaimer:</strong> This platform provides membership unit value tracking only.
            Value may change based on business valuation and approved internal assessments.
            All Global Access is not a financial institution. No guaranteed returns.
          </p>
        </div>

        <div className="mt-8">
          <DiscountClaimsCard mode="member" />
        </div>

        <div className="mt-8">
          <NewsWidget maxItems={4} compact />
        </div>
      </div>
      <UpgradePlanModal open={upgradeOpen} onOpenChange={setUpgradeOpen} onUpgraded={fetchDashboardData} />
    </MemberSidebarLayout>
  );
}

export default MemberDashboard;
